import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useOrganization } from '../../../contexts/OrganizationContext';
import customerService from '../../../utils/customerService';
import reviewAutomationService from '../../../utils/reviewAutomationService';
import ReviewStatusIndicator from '../../clients/components/ReviewStatusIndicator';

const CRMPipeline = ({ customers, onStageChange, onConfigureStages, onEditCustomer, onAddCustomer, onDeleteCustomer, onArchiveCustomer, onOpenCustomerView }) => {
  const [draggedCustomer, setDraggedCustomer] = useState(null);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragOverStage, setDragOverStage] = useState(null);
  const { currentOrganization } = useOrganization();

  // Load pipeline stages from database
  useEffect(() => {
    const loadPipelineStages = async () => {
      if (!currentOrganization?.id) return;
      
      try {
        setLoading(true);
        const stagesData = await customerService.getPipelineStages(currentOrganization.id);
        
        // Convert database stages to component format
        const formattedStages = stagesData.map(stage => ({
          id: stage.name.toLowerCase(),
          name: stage.name,
          color: getStageColorClass(stage.color),
          icon: stage.icon || 'Circle',
          dbId: stage.id,
          position: stage.position,
          triggers_review: stage.triggers_review || false
        }));
        
        setStages(formattedStages.sort((a, b) => a.position - b.position));
      } catch (error) {
        console.error('Error loading pipeline stages:', error);
        // Fallback to default stages if database load fails
        setStages([
          { id: 'lead', name: 'Lead', color: 'bg-blue-100 text-blue-800', icon: 'UserPlus' },
          { id: 'contacted', name: 'Contacted', color: 'bg-yellow-100 text-yellow-800', icon: 'Phone' },
          { id: 'qualified', name: 'Qualified', color: 'bg-purple-100 text-purple-800', icon: 'CheckCircle' },
          { id: 'proposal', name: 'Proposal Sent', color: 'bg-orange-100 text-orange-800', icon: 'FileText' },
          { id: 'negotiation', name: 'Negotiation', color: 'bg-red-100 text-red-800', icon: 'MessageSquare' },
          { id: 'won', name: 'Won', color: 'bg-green-100 text-green-800', icon: 'Trophy' },
          { id: 'lost', name: 'Lost', color: 'bg-gray-100 text-gray-800', icon: 'XCircle' }
        ]);
      } finally {
        setLoading(false);
      }
    };

    loadPipelineStages();
  }, [currentOrganization?.id]);

  // Convert hex color to Tailwind class
  const getStageColorClass = (hexColor) => {
    const colorMap = {
      '#3B82F6': 'bg-blue-100 text-blue-800',
      '#10B981': 'bg-green-100 text-green-800', 
      '#F59E0B': 'bg-yellow-100 text-yellow-800',
      '#EF4444': 'bg-red-100 text-red-800',
      '#8B5CF6': 'bg-purple-100 text-purple-800',
      '#F97316': 'bg-orange-100 text-orange-800',
      '#EC4899': 'bg-pink-100 text-pink-800',
      '#6366F1': 'bg-indigo-100 text-indigo-800',
      '#14B8A6': 'bg-teal-100 text-teal-800',
      '#6B7280': 'bg-gray-100 text-gray-800'
    };
    return colorMap[hexColor] || 'bg-gray-100 text-gray-800';
  };

  const getCustomersByStage = (stageName) => {
    return customers.filter(customer => 
      customer.pipeline_stage?.toLowerCase() === stageName.toLowerCase()
    );
  };

  const handleDragStart = (e, customer) => {
    setDraggedCustomer(customer);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', customer.id);
    
    // Add visual feedback
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    setDraggedCustomer(null);
    setDragOverStage(null);
    e.target.style.opacity = '1';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e, stageId) => {
    e.preventDefault();
    setDragOverStage(stageId);
  };

  const handleDragLeave = (e) => {
    // Only clear drag over state if we're leaving the drop zone entirely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverStage(null);
    }
  };

  const handleDrop = async (e, targetStage) => {
    e.preventDefault();
    setDragOverStage(null);
    
    if (!draggedCustomer) return;
    
    // Find the target stage data
    const targetStageData = stages.find(s => s.id === targetStage);
    if (!targetStageData) return;
    
    // Convert stage name to match database format
    const dbStageName = targetStageData.name;
    
    if (draggedCustomer.pipeline_stage === dbStageName) {
      setDraggedCustomer(null);
      return; // No change needed
    }
    
    try {
      // Update the customer's stage
      onStageChange(draggedCustomer.id, dbStageName);
      
      // Trigger review automation if the stage has review triggers
      if (targetStageData?.dbId) {
        try {
          const reviewResult = await reviewAutomationService.handleStageChange(
            draggedCustomer.id, 
            targetStageData.dbId, 
            dbStageName
          );
          
          // Handle review automation feedback
          if (reviewResult) {
            if (reviewResult.success) {
              if (reviewResult.scheduled) {
                console.log(`📅 Review request scheduled for ${reviewResult.delayHours} hours: ${reviewResult.scheduledSendTime}`);
                // Could show a success toast: "Review request scheduled to be sent in 24 hours"
              } else {
                console.log('✅ Review automation:', reviewResult.message);
                // Could show a success toast here if desired
              }
            } else {
              console.log('ℹ️ Review automation skipped:', reviewResult.message);
              
              // Show informative message based on reason
              if (reviewResult.reason === 'completed_review') {
                console.log(`📋 Customer has already completed a ${reviewResult.completedReview.rating}-star review`);
                // Could show a toast: "Customer has already completed a review - automatic request skipped"
              } else if (reviewResult.reason === 'active_request') {
                console.log('📋 Customer has pending review request - automatic request skipped');
              } else if (reviewResult.reason === 'recent_request') {
                console.log('📋 Review request sent recently - automatic request skipped');
              }
            }
          }
        } catch (error) {
          console.error('Error triggering review automation:', error);
          // Could show an error toast here if desired
        }
      }
    } catch (error) {
      console.error('Error updating customer stage:', error);
      // Could show an error toast here if desired
    }
    
    setDraggedCustomer(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const getCustomerDisplayName = (customer) => {
    if (customer.customer_type === 'organization' && customer.organization_name) {
      return customer.organization_name;
    }
    return customer.name;
  };

  const getCustomerIcon = (customer) => {
    return customer.customer_type === 'organization' ? 'Building' : 'User';
  };

  // Helper method to determine if a stage should be excluded from estimated value calculations
  const isClosedStage = (stageName) => {
    if (!stageName) return false;
    
    const closedStageKeywords = [
      'lost', 'cancelled', 'canceled', 'closed', 'rejected', 'declined', 
      'abandoned', 'expired', 'withdrawn', 'terminated', 'failed',
      'no-sale', 'no sale', 'dead', 'inactive'
    ];
    
    const normalizedStage = stageName.toLowerCase().trim();
    return closedStageKeywords.some(keyword => normalizedStage.includes(keyword));
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-text-secondary">Loading pipeline...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">CRM Pipeline</h3>
          <p className="text-sm text-text-secondary">Track customer journey stages</p>
        </div>
        <Button variant="outline" size="sm" onClick={onConfigureStages}>
          <Icon name="Settings" size={16} className="mr-2" />
          Configure Stages
        </Button>
      </div>

      {/* Horizontal scrolling container */}
      <div className="overflow-x-auto pb-4">
        <div className="flex space-x-6" style={{ minWidth: `${stages.length * 320}px` }}>
          {stages.map((stage) => {
            const stageCustomers = getCustomersByStage(stage.id);
            const isDragOver = dragOverStage === stage.id;
            
            return (
              <div
                key={stage.id}
                className={`bg-background rounded-lg border-2 p-4 h-[600px] w-80 flex-shrink-0 transition-all duration-200 flex flex-col ${
                  isDragOver 
                    ? 'border-primary bg-primary-50 shadow-lg' 
                    : 'border-border hover:border-border-hover'
                }`}
                onDragOver={handleDragOver}
                onDragEnter={(e) => handleDragEnter(e, stage.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, stage.id)}
              >
                {/* Stage Header */}
                <div className="flex items-center justify-between mb-4 flex-shrink-0">
                  <div className="flex items-center space-x-2">
                    <Icon name={stage.icon} size={16} className="text-text-muted" />
                    <h4 className="font-medium text-foreground">{stage.name}</h4>
                    {stage.triggers_review && (
                      <div 
                        className="inline-flex items-center px-2 py-1 rounded-full bg-yellow-100 border border-yellow-300"
                        title="This stage triggers automatic review requests"
                      >
                        <Icon name="Star" size={12} className="text-yellow-600 mr-1" />
                        <span className="text-xs font-medium text-yellow-700">Review Stage</span>
                      </div>
                    )}
                  </div>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${stage.color}`}>
                    {stageCustomers.length}
                  </span>
                </div>

                {/* Customer Cards */}
                <div className="space-y-3 flex-1 overflow-y-auto">
                  {stageCustomers.map((customer) => (
                    <div
                      key={customer.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, customer)}
                      onDragEnd={handleDragEnd}
                      className="bg-card border border-border rounded-lg p-3 cursor-move hover:shadow-md transition-all duration-200 hover:border-primary group relative"
                    >
                      {/* Header with Customer Info */}
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <Icon name={getCustomerIcon(customer)} size={16} className="text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h5 className="font-semibold text-foreground text-sm leading-tight">
                              {getCustomerDisplayName(customer)}
                            </h5>
                            {customer.customer_type === 'organization' && customer.organization_name && (
                              <p className="text-xs text-text-muted leading-tight">Contact: {customer.name}</p>
                            )}
                            <p className="text-xs text-text-muted">ID: {customer.id?.slice(0, 8)}...</p>
                          </div>
                        </div>
                        
                        {/* Hover-Revealed Actions */}
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center space-x-1 bg-card border border-border rounded-md shadow-lg p-1 z-10">
                          {onOpenCustomerView && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="p-1.5 text-text-muted hover:text-primary hover:bg-primary-50"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                console.log('View button clicked for customer:', customer.id);
                                onOpenCustomerView(customer);
                              }}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              title="Open Customer View"
                            >
                              <Icon name="Eye" size={12} />
                            </Button>
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="p-1.5 text-text-muted hover:text-blue-600 hover:bg-blue-50"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              console.log('Edit button clicked for customer:', customer.id);
                              onEditCustomer && onEditCustomer(customer);
                            }}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            title="Edit Customer"
                          >
                            <Icon name="Edit" size={12} />
                          </Button>
                          {onDeleteCustomer && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="p-1.5 text-text-muted hover:text-error hover:bg-error-50"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                console.log('Delete button clicked for customer:', customer.id);
                                onDeleteCustomer(customer);
                              }}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              title="Delete Customer"
                            >
                              <Icon name="Trash2" size={12} />
                            </Button>
                          )}
                          {onArchiveCustomer && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="p-1.5 text-text-muted hover:text-orange-600 hover:bg-orange-50"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                console.log('Archive button clicked for customer:', customer.id);
                                const customerName = getCustomerDisplayName(customer);
                                if (window.confirm(`Archive customer ${customerName}? This will move them to archived status.`)) {
                                  onArchiveCustomer(customer.id, 'Manual archive');
                                }
                              }}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              title="Archive Customer"
                            >
                              <Icon name="Archive" size={12} />
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Contact Information */}
                      <div className="space-y-1 text-xs text-text-secondary mb-3">
                        <div className="flex items-center space-x-2">
                          <Icon name="Mail" size={12} className="text-text-muted flex-shrink-0" />
                          <span className="truncate">{customer.email || 'N/A'}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Icon name="Phone" size={12} className="text-text-muted flex-shrink-0" />
                          <span className="truncate">{customer.phone || 'N/A'}</span>
                        </div>
                      </div>

                      {/* Timeline Information */}
                      {(customer.last_contact_date || customer.next_follow_up) && (
                        <div className="space-y-1 text-xs text-text-secondary mb-3">
                        {customer.last_contact_date && (
                            <div className="flex items-center space-x-2">
                              <Icon name="Clock" size={12} className="text-text-muted flex-shrink-0" />
                            <span>Last: {formatDate(customer.last_contact_date)}</span>
                          </div>
                        )}
                        {customer.next_follow_up && (
                            <div className="flex items-center space-x-2">
                              <Icon name="Calendar" size={12} className="text-text-muted flex-shrink-0" />
                            <span>Next: {formatDate(customer.next_follow_up)}</span>
                          </div>
                        )}
                      </div>
                      )}

                      {/* Priority Indicator */}
                      {customer.pipeline_priority && customer.pipeline_priority !== 'medium' && (
                        <div className="mb-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            customer.pipeline_priority === 'high' ? 'bg-red-100 text-red-800' :
                            customer.pipeline_priority === 'urgent' ? 'bg-red-200 text-red-900' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {customer.pipeline_priority === 'urgent' && <Icon name="AlertTriangle" size={10} className="mr-1" />}
                            {customer.pipeline_priority.charAt(0).toUpperCase() + customer.pipeline_priority.slice(1)}
                          </span>
                        </div>
                      )}

                      {/* Review Status */}
                      <div className="mb-3">
                        <ReviewStatusIndicator customerId={customer.id} size="default" />
                      </div>

                      {/* Footer with Value and Quick Actions */}
                      <div className="flex items-center justify-between pt-2 border-t border-border">
                        <div className="flex items-center space-x-1">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="p-1.5 text-text-muted hover:text-blue-600 hover:bg-blue-50"
                            title="Call Customer"
                          >
                            <Icon name="Phone" size={12} />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="p-1.5 text-text-muted hover:text-green-600 hover:bg-green-50"
                            title="Email Customer"
                          >
                            <Icon name="Mail" size={12} />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="p-1.5 text-text-muted hover:text-purple-600 hover:bg-purple-50"
                            title="Schedule Appointment"
                          >
                            <Icon name="Calendar" size={12} />
                          </Button>
                        </div>
                        {customer.estimated_value && (
                          <div className="text-right">
                          <span className="text-xs font-medium text-success">
                            ${customer.estimated_value.toLocaleString()}
                          </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Empty State */}
                  {stageCustomers.length === 0 && (
                    <div className="text-center py-8">
                      <Icon name="Users" size={32} className="text-text-muted mx-auto mb-2" />
                      <p className="text-sm text-text-muted">No customers in this stage</p>
                    </div>
                  )}
                </div>

                {/* Add Customer Button */}
                <div className="mt-4 flex-shrink-0">
                  <Button
                    variant="ghost"
                    className="w-full border-2 border-dashed border-border hover:border-primary hover:bg-primary-50 transition-all duration-200"
                    onClick={() => onAddCustomer && onAddCustomer(stage.name)}
                  >
                    <Icon name="Plus" size={16} className="mr-2" />
                    Add Customer
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Pipeline Stats */}
      <div className="mt-6 pt-6 border-t border-border">
        <div className="overflow-x-auto">
          <div className="flex space-x-8" style={{ minWidth: `${stages.length * 120}px` }}>
            {stages.map((stage) => {
              const stageCustomers = getCustomersByStage(stage.id);
              const totalValue = stageCustomers.reduce((sum, customer) => {
                // Only include estimated value if the stage is not closed and customer is not archived
                if (customer.is_archived || isClosedStage(stage.name)) {
                  return sum;
                }
                return sum + (customer.estimated_value || 0);
              }, 0);

              return (
                <div key={stage.id} className="text-center flex-shrink-0">
                  <div className="text-2xl font-bold text-foreground">{stageCustomers.length}</div>
                  <div className="text-sm text-text-muted">{stage.name}</div>
                  {totalValue > 0 && (
                    <div className="text-xs text-success font-medium">${totalValue.toLocaleString()}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CRMPipeline;